import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DocumentUpload } from '../components/DocumentUpload';
import { api } from '../App';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function MembershipUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/api/membership/upload/${token}`).then(setInfo).catch(e => setError(e.message)); }, [token]);

  const submit = async () => {
    if (!files.length) { toast.error('Please choose a file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', files[0]);
      const res = await fetch(`${BACKEND}/api/membership/upload/${token}`, { method: 'POST', body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `Upload failed (error ${res.status}). Please try again — if it keeps happening, contact us and we will help.`);
      setDone(true); toast.success('Thank you - your file has been received securely');
    } catch (e) { toast.error(/failed to fetch|load failed|network/i.test(e.message) ? 'Connection dropped while sending the file. Please check your signal and try again.' : e.message, { duration: 8000 }); }
    setBusy(false);
  };

  const isPhoto = info?.purpose === 'photo';
  return (
    <div className="min-h-[70vh] bg-[hsl(var(--lux-ivory))] py-12 px-4" data-testid="membership-upload-page">
      <div className="max-w-lg mx-auto">
        <Card className="border-2 border-[hsl(var(--lux-gold))]">
          <CardContent className="p-7">
            {error ? (
              <div className="text-center" data-testid="membership-upload-error"><p className="text-red-800 font-medium">{error}</p><Link to="/contact"><Button className="mt-4" variant="outline">Contact us</Button></Link></div>
            ) : !info ? (
              <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : done ? (
              <div className="text-center" data-testid="membership-upload-done">
                <ShieldCheck className="w-12 h-12 mx-auto text-[hsl(var(--lux-royal))]" />
                <h2 className="mt-3 text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>Thank you, {info.first_name}</h2>
                <p className="mt-2 text-sm text-[hsl(var(--lux-ink)/0.8)]">{isPhoto ? 'Your photograph has been received and your card will be updated.' : info.returning_member ? 'Your document has been received securely and will be checked shortly. Your membership number and status are unchanged.' : 'Your document has been received securely and will be checked shortly. You will receive an email once your membership is activated.'}</p>
                <Link to="/"><Button className="mt-5 bg-[hsl(var(--lux-royal))] text-white">Back to home</Button></Link>
              </div>
            ) : (
              <>
                <p className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--lux-gold))]">{info.returning_member ? 'Existing member' : `${info.membership_type} membership`}</p>
                <h1 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mt-1" style={{ fontFamily: 'Cormorant Garamond' }}>{isPhoto ? 'Upload your photograph' : info.returning_member ? 'Upload your identity document' : 'Upload a new identity document'}</h1>
                <p className="text-sm text-[hsl(var(--lux-ink)/0.8)] mt-2 mb-5">Hello {info.first_name}. {isPhoto ? 'Please upload a clear, recent head-and-shoulders photo for your membership card. Plain background, no sunglasses or hats.' : 'Please upload a clear photo or scan of a document that shows your full name and date of birth exactly as registered (passport, driving licence or similar).'}{info.returning_member && !isPhoto ? ' Your existing membership number stays the same — no new registration or payment is needed.' : ''}</p>
                <DocumentUpload files={files} onChange={setFiles} maxFiles={1} maxSizeMb={30} accept={isPhoto ? 'images' : 'documents+images'} label="" required testId="membership-token-upload" />
                <Button className="mt-5 w-full bg-[hsl(var(--lux-royal))] text-white" disabled={busy || !files.length} onClick={submit} data-testid="membership-token-upload-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Send securely</Button>
                <p className="mt-3 text-[11px] text-[hsl(var(--lux-ink)/0.55)]">Files are stored privately and viewed only by authorised administrators, in line with our Privacy Policy.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
